/**
 * Central display format functions and templates for the entire app.
 * All dropdown labels and table cell display values should use these
 * so that a format change is a single-line edit.
 *
 * This file has ZERO app imports — it is a pure leaf-node utility.
 * Entity configs, composables, and components all import from here.
 */

// ============================================================================
// Table cell display format templates (used in entity config displayFormat)
// These use {FIELD_NAME} placeholders resolved at runtime by DataTable.
// ============================================================================

export const TABLE_PRODUCT = '{ORIGIN_PRODUCT_ID}: {PRODUCT_ID}'
export const TABLE_SERVICE = '{SERVICE_ID}: {URI}'
export const TABLE_SERVICE_PARAM = '{SERVICE_PARAM_ID}: {PARAM_NAME}'
export const TABLE_STEP_TYPE = '{STEP_TYPE_ID}: {STEP_TYPE_NAME}'
export const TABLE_SERVICE_PROVIDER = '{SERVICE_PROVIDER_ID}: {SERVICE_PROVIDER_NAME}'
export const TABLE_SERVICE_RESPONSE = '{SERVICE_RESPONSE_ID}: {RESPONSE_PATH}'
export const TABLE_URL_TYPE = '{URL_TYPE_CODE}'

// ============================================================================
// Dropdown label functions (used by composables and components)
// ============================================================================

/** Service: SERVICE_PROVIDER_NAME: URI (SERVICE_ID) */
export function formatServiceLabel(serviceId: number, uri: string, providerName?: string): string {
  return `${providerName || ''}: ${uri} (${serviceId})`
}

/** Product: PRODUCT_ID: PRODUCT_DESC (ORIGIN_PRODUCT_ID) */
export function formatProductLabel(productId: number, productCode: string, productDesc?: string): string {
  return `${productCode}: ${productDesc || ''} (${productId})`
}

/** Service Param: SERVICE_PARAM_ID: PARAM_NAME */
export function formatServiceParamLabel(paramId: number, paramName: string): string {
  return `${paramId}: ${paramName}`
}

/** Step Type: STEP_TYPE_ID: STEP_TYPE_NAME */
export function formatStepTypeLabel(stepTypeId: number, stepTypeName?: string): string {
  return `${stepTypeId}: ${stepTypeName || 'Unknown'}`
}

/** Service Provider: SERVICE_PROVIDER_ID: SERVICE_PROVIDER_NAME */
export function formatServiceProviderLabel(providerId: number, providerName: string): string {
  return `${providerId}: ${providerName}`
}

/** Service Response: SERVICE_RESPONSE_ID: RESPONSE_PATH */
export function formatServiceResponseLabel(responseId: number, responsePath: string): string {
  return `${responseId}: ${responsePath}`
}

/** Service Param Mapping: ID: PRODUCT_ID - SOURCE_PARAM -> TARGET_PARAM */
export function formatParamMappingLabel(mappingId: number, productId: string, sourceParam: string, targetParam: string): string {
  return `${mappingId}: ${productId || 'N/A'} - ${sourceParam || 'N/A'} \u2192 ${targetParam || 'N/A'}`
}

// ============================================================================
// Table cell inline formatters (used by useOptimizedQueries for enhanced display)
// These produce the same format as the TABLE_* templates above but from raw values.
// ============================================================================

/** Format a table cell value: ID: displayValue */
export function formatTableCell(id: number | string, displayValue: string): string {
  return `${id}: ${displayValue}`
}

// ============================================================================
// Sorting helper for dropdown options
// ============================================================================

/**
 * Extract the sortable text from a display label.
 * For labels like "123: Some Name", returns "Some Name".
 * For labels like "(123) - /api/path (Provider)", returns "/api/path (Provider)".
 * Used to sort dropdowns by the meaningful text, not the numeric ID.
 */
export function getSortableText(label: string): string {
  if (typeof label !== 'string') return ''
  // Handle "ID: text" format
  if (label.includes(':')) {
    return label.split(':').slice(1).join(':').trim()
  }
  // Handle "(ID) - text" format
  if (label.startsWith('(')) {
    const dashIdx = label.indexOf(' - ')
    if (dashIdx > 0) return label.substring(dashIdx + 3).trim()
  }
  return label
}

/**
 * Sort an array of dropdown options by the meaningful text portion of their label.
 * Sorts case-insensitively by the text after the first colon (or after "(ID) - ").
 */
export function sortOptionsByLabel<T extends { label: string }>(options: T[]): T[] {
  return [...options].sort((a, b) =>
    getSortableText(a.label).localeCompare(getSortableText(b.label), undefined, { sensitivity: 'base' })
  )
}
